import React from "react"
import { FormikProvider, useFormik, useFormikContext } from "formik"
import { observer, useLocalObservable } from "mobx-react-lite"
import * as yup from "yup"
import clsx from "clsx"

import { useController, useStore } from "@store/index"
import SearchInput from "@pages/launch-solutions/SolutionTabsContainer/SearchInput"
import { useSolution } from "@pages/launch-solutions/ActiveSolutionContext"
import useActiveAvatar from "@pages/search/ActiveAvatarContext/useActiveAvatar"
import ShiftContainer from "@components/ui/ShiftContainer/ShiftContainer"
import { getReportDescriptionByType } from "@framework/constants/workbook"
import useClickOutside from "@components/hooks/useOutsideClick"
import { Option } from "@framework/types/utils"
import { WorkbookReportTypeName } from "@framework/types/workbook-report"
import Icon from "@components/ui/Icon/Icon"
import Button from "@components/ui/Button/Button"
import List from "@components/ui/List/List"
import Chip from "@components/ui/Chip/Chip"
import Checkbox from "@components/ui/Checkbox/Checkbox"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/Tooltip/v2/Tooltip"

import WorkbookReportTemplate from "./WorkbookReportTemplate"
import {
  SearchWidgetContextState,
  SearchWidgetProvider,
  useSearchWidget,
} from "./SearchWidgetContext"

import styles from "./SearchWidget.module.sass"

const validationSchema = yup.object({
  query: yup.string().trim().min(1).default(""),
})

export interface Props {
  loading?: boolean
}

export const SearchWidget: React.FC<Props> = observer(({ loading }) => {
  const { solution } = useSolution()

  const { avatar } = useActiveAvatar()

  const {
    solutionsStore: { appliedFilters },
    productComparisonStore: { productStore },
    factFinderSolutionStore: { searchFlowStore },
    contentManagerStore: { companies },
  } = useStore()

  const { factFinderSolutionController: controller } = useController()

  const rootRef = React.useRef<HTMLDivElement | null>(null)

  const searchWidgetContext = useLocalObservable(
    () => new SearchWidgetContextState()
  )

  const handleSubmit = (data: any) => {
    if (solution?.id)
      controller.search(
        data.query,
        avatar,
        solution.id,
        appliedFilters,
        companies
      )
    formik.setFieldValue("query", "")
  }

  const formik = useFormik({
    initialValues: validationSchema.getDefault(),
    validationSchema,
    validateOnChange: true,
    onSubmit: handleSubmit,
  })

  const isSearching = searchFlowStore.isAnyLoading()

  React.useEffect(() => {
    productStore.loadProducts({
      filters: { search: "" },
      pagination: { ...productStore.productsMetaData, pageSize: 500 },
    })
  }, [])

  React.useEffect(() => {
    if (loading) searchWidgetContext.setActive(false)
  }, [loading])

  React.useEffect(() => {
    if (searchFlowStore.isAnyLoading()) formik.resetForm()
  }, [searchFlowStore?.flowSequence?.length])

  React.useLayoutEffect(() => {
    if (searchFlowStore.reportCandidate) {
      searchWidgetContext.setActive(true)
    }
  }, [searchFlowStore.reportCandidate])

  React.useEffect(() => {
    controller.loadAutocomplete("")
  }, [])

  useClickOutside({
    element: rootRef,
    onClickOutside: () => searchWidgetContext.setActive(false),
    onClickInside: () => searchWidgetContext.setActive(true),
  })

  return (
    <FormikProvider value={formik}>
      <SearchWidgetProvider value={searchWidgetContext}>
        <div
          ref={rootRef}
          className={clsx(styles.root, {
            [styles.focused]: searchWidgetContext.isActive,
          })}
        >
          <div className={styles.box}>
            <WorkflowHeader />

            <ShiftContainer>
              {searchFlowStore.reportCandidate ? (
                <WorkbookReportTemplate
                  loading={loading}
                  reportSuggestion={searchFlowStore.reportCandidate}
                  onSubmit={() => {
                    searchFlowStore.generateReport(null)
                  }}
                  onCancel={() => {
                    searchFlowStore.generateReport(null)
                  }}
                  key={searchFlowStore.reportCandidate.type}
                />
              ) : (
                <SearchForm loading={isSearching} key="search" />
              )}
            </ShiftContainer>
          </div>

          <SettingsFooter />
        </div>
      </SearchWidgetProvider>
    </FormikProvider>
  )
})

export const WorkflowHeader: React.FC<Props> = observer(({ loading }) => {
  const {
    factFinderSolutionStore: { searchFlowStore, searchAutocomplete },
  } = useStore()

  const options: Option[] = React.useMemo(
    () => [
      {
        name: "PRODUCTQUERY",
        value: "Product Query",
        icon: "search",
      },
      ...searchAutocomplete.data.map((it) => {
        const item = getReportDescriptionByType(it.type)
        return {
          name: it.type,
          value: item.displayName,
          icon: item.icon,
        }
      }),
    ],
    [searchAutocomplete.data]
  )

  React.useEffect(() => {
    const selectedWorkflow = searchFlowStore.reportCandidate
    if (
      selectedWorkflow != null &&
      !options.find((it) => it.name === selectedWorkflow.type)
    )
      searchFlowStore.generateReport(null)
  }, [options])

  return (
    <div className={styles.header}>
      <List direction="row" gutter="8" align="center">
        {options.map((it) => {
          const isActive =
            it.name ===
            (searchFlowStore.reportCandidate != null
              ? searchFlowStore.reportCandidate.type
              : options[0].name)

          return (
            <Button
              title={it.value}
              color="default"
              variant={isActive ? "contained" : "outlined"}
              size={isActive ? "medium" : "small"}
              before={<Icon name={it.icon} />}
              disabled={loading}
              onClick={() => {
                if (it.name === options[0].name)
                  searchFlowStore.generateReport(null)
                else
                  searchFlowStore.generateReport({
                    type: it.name as WorkbookReportTypeName,
                    data: {},
                  })
              }}
              key={it.name}
            >
              <span className={styles.nowrap}>{it.value}</span>
            </Button>
          )
        })}
      </List>
    </div>
  )
})

export const SearchForm: React.FC<Props> = observer(({ loading }) => {
  const formik = useFormikContext()

  return (
    <form onSubmit={formik.handleSubmit}>
      <section className={styles.searchContainer}>
        <SearchInput loading={loading} />
      </section>
    </form>
  )
})

const SettingsFooter: React.FC = observer(() => {
  const {
    restrictionsStore: access,
    factFinderSolutionStore: { searchFlowStore },
  } = useStore()

  const searchWidgetContext = useSearchWidget()

  React.useEffect(() => {
    if (searchFlowStore.reportCandidate != null)
      searchWidgetContext.showSettings(false)
  }, [searchFlowStore.reportCandidate])

  const hasSettings = access.allowWebSearch
  const active = searchWidgetContext.isSettingsShown && hasSettings

  return (
    <div
      className={clsx(styles.settingsFooter, {
        [styles.visible]: active,
      })}
    >
      <div className={styles.body}>
        <Tooltip color="primary" delay={600} closeOnTargetPress>
          <TooltipTrigger>
            <Chip
              variant="rounded"
              className={styles.toolChip}
              before={
                <Checkbox
                  size="tiny"
                  checked={searchFlowStore.isWebSearchEnabled}
                />
              }
              color={
                searchFlowStore.isWebSearchEnabled
                  ? "primary"
                  : "secondary-solid"
              }
              onClick={() => {
                searchFlowStore.enableWebSearch(
                  !searchFlowStore.isWebSearchEnabled
                )
              }}
            >
              Web Research
            </Chip>
          </TooltipTrigger>
          <TooltipContent>
            Include targeted web sources to search
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
})

export default SearchWidget
